// Therapist UI Styling Constants
// Leverages existing AIMHI dashboard theme for consistency
import { dashboardTheme } from '../../v2/styles/dashboardTheme';

export const therapistStyles = {
  // Main container card styling
  mainCard: {
    borderRadius: '16px',
    background: '#ffffff',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: dashboardTheme.shadows.medium,
    transition: dashboardTheme.animations.transition,
    overflow: 'hidden',
    '&:hover': {
      boxShadow: dashboardTheme.shadows.large
    }
  },

  // Smaller cards for nested content
  contentCard: {
    borderRadius: '12px',
    background: '#ffffff',
    border: '1px solid rgba(0,0,0,0.04)',
    boxShadow: dashboardTheme.shadows.small,
    transition: dashboardTheme.animations.transition,
    p: 2
  },

  // Tab styling for dashboard
  tabContainer: {
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    backgroundColor: '#fafafa',
    borderRadius: '16px 16px 0 0',
    px: 2
  },

  tab: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.95rem',
    color: '#64748b',
    transition: 'all 0.3s',
    '&.Mui-selected': {
      color: dashboardTheme.colors.primary.main,
      fontWeight: 600
    },
    '&:hover': {
      color: dashboardTheme.colors.primary.dark,
      backgroundColor: 'rgba(255,140,0,0.04)'
    }
  },

  // Table styling
  tableContainer: {
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: dashboardTheme.shadows.small
  },

  tableHeader: {
    backgroundColor: '#f8f9fa',
    '& th': {
      fontWeight: 600,
      color: '#1a1a1a',
      borderBottom: '2px solid rgba(255,140,0,0.2)'
    }
  },

  tableRow: {
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(255,140,0,0.02)',
      transform: 'translateX(2px)'
    },
    '&:last-child td, &:last-child th': {
      border: 0
    }
  },

  // Message styling for conversations
  messageContainer: {
    display: 'flex',
    gap: 1,
    mb: 2,
    animation: 'fadeIn 0.3s ease-in'
  },

  aiMessage: {
    backgroundColor: '#f5f5f5',
    borderRadius: '12px',
    p: 2,
    maxWidth: '70%',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },

  therapistMessage: {
    backgroundColor: 'rgba(255,140,0,0.08)',
    borderRadius: '12px',
    p: 2,
    maxWidth: '70%',
    border: '1px solid rgba(255,140,0,0.1)',
    boxShadow: '0 1px 3px rgba(255,140,0,0.1)',
    ml: 'auto'
  },

  clientMessage: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    p: 2,
    maxWidth: '70%',
    border: '1px solid rgba(96,125,139,0.2)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },

  // Mode indicators
  modeChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 28,
    transition: 'all 0.3s'
  },

  aiModeChip: {
    backgroundColor: '#E3F2FD',
    color: '#1976D2',
    border: '1px solid #90CAF9',
    '&:hover': {
      backgroundColor: '#BBDEFB'
    }
  },

  humanModeChip: {
    backgroundColor: '#E8F5E9',
    color: '#388E3C',
    border: '1px solid #81C784',
    '&:hover': {
      backgroundColor: '#C8E6C9'
    }
  },

  assistedModeChip: {
    backgroundColor: '#FFF3E0',
    color: '#F57C00',
    border: '1px solid #FFB74D',
    '&:hover': {
      backgroundColor: '#FFE0B2'
    }
  },

  // Button styles
  primaryButton: {
    background: dashboardTheme.colors.primary.gradient,
    color: '#ffffff',
    fontWeight: 600,
    borderRadius: '12px',
    textTransform: 'none',
    px: 3,
    py: 1,
    boxShadow: dashboardTheme.shadows.colored(dashboardTheme.colors.primary.main),
    transition: dashboardTheme.animations.transition,
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: dashboardTheme.shadows.glow(dashboardTheme.colors.primary.main)
    },
    '&:active': {
      transform: 'translateY(0)'
    }
  },

  secondaryButton: {
    backgroundColor: '#ffffff',
    color: dashboardTheme.colors.primary.main,
    fontWeight: 600,
    borderRadius: '12px',
    textTransform: 'none',
    border: `2px solid ${dashboardTheme.colors.primary.main}`,
    px: 3,
    py: 0.75,
    transition: dashboardTheme.animations.transition,
    '&:hover': {
      backgroundColor: 'rgba(255,140,0,0.04)',
      transform: 'translateY(-1px)'
    }
  },

  outlinedButton: {
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 500,
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.02)',
      borderWidth: '2px'
    }
  },

  // Status indicators
  statusChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24
  },

  activeStatus: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
    border: '1px solid #66BB6A'
  },

  reviewStatus: {
    backgroundColor: '#FFF3E0',
    color: '#E65100',
    border: '1px solid #FF9800',
    animation: 'pulse 2s infinite'
  },

  alertStatus: {
    backgroundColor: '#FFEBEE',
    color: '#C62828',
    border: '1px solid #EF5350'
  },

  // AI Suggestion cards
  suggestionCard: {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)',
    borderRadius: '12px',
    p: 2,
    mb: 2,
    border: '1px solid rgba(0,0,0,0.05)',
    position: 'relative',
    animation: 'slideIn 0.4s ease-out',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '4px',
      height: '100%',
      background: dashboardTheme.colors.info.gradient,
      borderRadius: '12px 0 0 12px'
    }
  },

  goalProposalCard: {
    background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
    borderRadius: '12px',
    p: 2,
    mb: 2,
    border: '1px solid rgba(76,175,80,0.2)',
    position: 'relative',
    animation: 'slideIn 0.4s ease-out',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '4px',
      height: '100%',
      background: dashboardTheme.colors.success.gradient,
      borderRadius: '12px 0 0 12px'
    }
  },

  // Care note alert styling
  careNoteAlert: {
    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
    borderRadius: '12px',
    p: 2,
    mb: 2,
    border: '1px solid rgba(255,152,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    animation: 'slideDown 0.4s ease-out'
  },

  // Section headers
  sectionHeader: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#1a1a1a',
    mb: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    '&::after': {
      content: '""',
      flex: 1,
      height: '1px',
      background: 'linear-gradient(90deg, rgba(0,0,0,0.1) 0%, transparent 100%)',
      ml: 2
    }
  },

  // Consultation history card
  consultationCard: {
    borderRadius: '12px',
    p: 2,
    mb: 2,
    backgroundColor: '#ffffff',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: dashboardTheme.shadows.small,
    transition: 'all 0.3s',
    '&:hover': {
      boxShadow: dashboardTheme.shadows.medium,
      transform: 'translateY(-2px)'
    }
  },

  // Admin switch button (for users with both roles)
  adminSwitchButton: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#ffffff',
    fontWeight: 600,
    borderRadius: '12px',
    textTransform: 'none',
    px: 2,
    py: 1,
    boxShadow: '0 4px 12px rgba(102,126,234,0.3)',
    transition: 'all 0.3s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(102,126,234,0.4)'
    }
  }
};

// Animations
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes slideIn {
      from {
        opacity: 0;
        transform: translateX(-20px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes pulse {
      0% { opacity: 1; }
      50% { opacity: 0.6; }
      100% { opacity: 1; }
    }

    /* Phase 3 a11y — honour reduced-motion: disable the animated affordances
       (e.g. the pulsing Critical chip) for users who request reduced motion. */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
        scroll-behavior: auto !important;
      }
    }
  `;
  document.head.appendChild(styleSheet);
}

// Re-export design tokens so components can adopt them via the styles entrypoint
// (introduced ticket 1.7; canonical source is ./tokens.js).
export { tokens, statusColor } from './tokens';

export default therapistStyles;